/**
 * AI Action Buttons Component
 * 
 * Displays action buttons based on AI suggestions
 * with human-in-the-loop confirmation
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
  Stack,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Archive as ArchiveIcon,
  Reply as ReplyIcon,
  Flag as FlagIcon,
  Label as TagIcon,
  AssignmentTurnedIn as TaskIcon,
  Phone as CallIcon,
  Warning as EscalateIcon,
  Unsubscribe as UnsubscribeIcon,
  PersonAdd as AssignIcon,
  Refresh as RefreshIcon,
  Check as CheckIcon,
} from '@mui/icons-material';

// Action configuration
const ACTION_CONFIG = {
  archive: {
    label: 'Archivia',
    icon: ArchiveIcon,
    color: 'inherit',
    description: 'Sposta l\'email nella cartella Archivio',
  },
  reply_draft: {
    label: 'Inserisci Bozza',
    icon: ReplyIcon,
    color: 'primary',
    description: 'Inserisci la bozza di risposta suggerita',
  },
  respond: {
    label: 'Rispondi',
    icon: ReplyIcon,
    color: 'primary',
    description: 'Rispondi all\'email',
  },
  create_task: {
    label: 'Crea Task',
    icon: TaskIcon,
    color: 'secondary',
    description: 'Crea un task/promemoria per questa email',
  },
  tag: {
    label: 'Aggiungi Tag',
    icon: TagIcon,
    color: 'info',
    description: 'Aggiungi i tag suggeriti all\'email',
  },
  escalate: {
    label: 'Scala',
    icon: EscalateIcon,
    color: 'warning',
    description: 'Escalation: richiede attenzione urgente',
  },
  schedule_call: {
    label: 'Pianifica Chiamata',
    icon: CallIcon,
    color: 'success',
    description: 'Pianifica una chiamata con il cliente',
  },
  unsubscribe: {
    label: 'Disiscrivi',
    icon: UnsubscribeIcon,
    color: 'error',
    description: 'Cerca link di disiscrizione nell\'email',
  },
  flag: {
    label: 'Contrassegna',
    icon: FlagIcon,
    color: 'warning',
    description: 'Contrassegna l\'email come importante',
  },
  assign_customer: {
    label: 'Associa Cliente',
    icon: AssignIcon,
    color: 'primary',
    description: 'Associa l\'email a un cliente esistente',
  },
  update_customer_profile: {
    label: 'Aggiorna Cliente',
    icon: AssignIcon,
    color: 'info',
    description: 'Aggiorna il profilo del cliente',
  },
  forward: {
    label: 'Inoltra',
    icon: ReplyIcon,
    color: 'inherit',
    description: 'Inoltra l\'email',
  },
  delete: {
    label: 'Elimina',
    icon: ArchiveIcon,
    color: 'error',
    description: 'Elimina l\'email',
  },
};

// Priority colors
const PRIORITY_COLORS = {
  high: 'error',
  medium: 'warning',
  low: 'default',
};

// Safe get priority color - returns 'default' for invalid/undefined priorities
const getPriorityColor = (priority) => {
  return PRIORITY_COLORS[priority] || 'default';
};

/**
 * Single Action Button
 */
function ActionButton({ 
  action, 
  priority, 
  reason, 
  onApply, 
  loading, 
  applied,
  compact = false 
}) {
  const config = ACTION_CONFIG[action] || {
    label: action || 'Azione',
    icon: CheckIcon,
    color: 'inherit',
    description: '',
  };
  const Icon = config.icon;

  const tooltipContent = (
    <Box>
      <Typography variant="body2" fontWeight="bold">{config.label}</Typography>
      <Typography variant="caption" display="block">{config.description}</Typography>
      {reason && (
        <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
          Motivo: {reason}
        </Typography>
      )}
      {priority && (
        <Chip 
          size="small" 
          label={`Priorità: ${priority}`} 
          color={getPriorityColor(priority)}
          sx={{ mt: 0.5 }}
        />
      )}
    </Box>
  );

  if (applied) {
    return (
      <Chip
        size="small"
        icon={<CheckIcon />}
        label={config.label}
        color="success"
        variant="outlined"
      />
    );
  }

  return (
    <Tooltip title={tooltipContent} arrow placement="top">
      <Button
        size={compact ? 'small' : 'medium'}
        variant={priority === 'high' ? 'contained' : 'outlined'}
        color={config.color}
        startIcon={loading ? <CircularProgress size={16} /> : <Icon />}
        onClick={() => onApply(action)}
        disabled={loading}
        sx={{
          textTransform: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        {compact ? '' : config.label}
      </Button>
    </Tooltip>
  );
}

/**
 * AiActionButtons - Displays suggested actions
 * 
 * @param {Array} suggestedActions - Array of { action, priority, reason }
 * @param {function} onApplyAction - Handler when action is applied
 * @param {function} onRegenerate - Handler to regenerate suggestions
 * @param {function} onUpgradeModel - Handler to use advanced model
 * @param {boolean} loading - Loading state
 * @param {string} currentModel - Current AI model being used
 * @param {boolean} compact - Use compact layout
 */
export default function AiActionButtons({
  suggestedActions = [],
  onApplyAction,
  onRegenerate,
  loading = false,
  compact = false,
  appliedActions = [],
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [applyingAction, setApplyingAction] = useState(null);

  const handleApplyClick = (action) => {
    // Actions that need confirmation
    const needsConfirmation = ['archive', 'escalate', 'unsubscribe'];
    
    if (needsConfirmation.includes(action)) {
      setConfirmDialog(action);
    } else {
      handleApplyAction(action);
    }
  };

  const handleApplyAction = async (action) => {
    setConfirmDialog(null);
    setApplyingAction(action);
    
    try {
      if (onApplyAction) {
        await onApplyAction(action);
      }
    } finally {
      setApplyingAction(null);
    }
  };

  // Sort actions by priority
  const sortedActions = [...suggestedActions].sort((a, b) => {
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    return (priorityOrder[a.priority] || 2) - (priorityOrder[b.priority] || 2);
  });

  // Separate primary (high priority) from secondary actions
  const primaryActions = sortedActions.filter(a => a.priority === 'high');
  const secondaryActions = sortedActions.filter(a => a.priority !== 'high');

  if (suggestedActions.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        Nessuna azione suggerita per questa email.
      </Alert>
    );
  }

  return (
    <>
      <Box>
        {/* Primary Actions */}
        {primaryActions.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
            {primaryActions.map((actionItem, index) => (
              <ActionButton
                key={index}
                action={actionItem.action}
                priority={actionItem.priority}
                reason={actionItem.reason}
                onApply={handleApplyClick}
                loading={applyingAction === actionItem.action || loading}
                applied={appliedActions.includes(actionItem.action)}
                compact={compact}
              />
            ))}
          </Stack>
        )}

        {/* Secondary Actions */}
        {secondaryActions.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
            {secondaryActions.map((actionItem, index) => (
              <ActionButton
                key={index}
                action={actionItem.action}
                priority={actionItem.priority}
                reason={actionItem.reason}
                onApply={handleApplyClick}
                loading={applyingAction === actionItem.action || loading}
                applied={appliedActions.includes(actionItem.action)}
                compact={compact}
              />
            ))}
          </Stack>
        )}

        {/* Utility Buttons */}
        <Stack direction="row" spacing={1} sx={{ mt: 2, pt: 1, borderTop: '1px solid #eee' }}>
          <Tooltip title="Rigenera suggerimenti">
            <Button
              size="small"
              variant="text"
              startIcon={<RefreshIcon />}
              onClick={onRegenerate}
              disabled={loading}
            >
              Rigenera
            </Button>
          </Tooltip>
          

        </Stack>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Conferma Azione
        </DialogTitle>
        <DialogContent>
          <Typography>
            Sei sicuro di voler eseguire l'azione "{ACTION_CONFIG[confirmDialog]?.label}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {ACTION_CONFIG[confirmDialog]?.description}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(null)}>
            Annulla
          </Button>
          <Button 
            variant="contained" 
            onClick={() => handleApplyAction(confirmDialog)}
            color={ACTION_CONFIG[confirmDialog]?.color || 'primary'}
          >
            Conferma
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// Export config for external use
export { ACTION_CONFIG, PRIORITY_COLORS, getPriorityColor };
